// Tests: src/app/migrations/migration-v16-to-v17.spec.ts
import { MigrationDefinition } from './migration.types';

/**
 * Migration: V16 → V17
 *
 * Bereinigt zwei Workflow-mäßig nicht unterstützte Alt-Zustände von
 * Punkt-Dokumenten (User-Befund 2026-06-16):
 *
 *  1. **Mehrfach-Projekt-Zuordnung:** `header.projects` mit mehr als einem
 *     Eintrag wird auf EIN Projekt reduziert — bevorzugt das per
 *     `workflow.projectId` zugeordnete (falls im Array vorhanden), sonst das
 *     LETZTE im Array. Hinweis: Das „aktuell offene Projekt" ist in einer
 *     Per-Doc-DB-Migration nicht bekannt (kein UI-Kontext, `upgrade` bekommt nur
 *     das Doc) — `workflow.projectId` dient daher als „aktuelles Projekt dieses
 *     Punkts".
 *
 *  2. **Bestand mit Projekt:** Ein Punkt ohne `workflow` (Bestand), der aber
 *     einem Projekt zugeordnet ist, bekommt einen echten Workflow-Block mit
 *     Status `abgeschlossen` (projectId = verbleibendes Projekt). „Bestand" ist
 *     als Zustand abgeschafft (2026-06-16) — der Punkt zeigt damit sauber
 *     „Abgeschlossen". Projektlose Altdaten ohne Status fängt zusätzlich das
 *     Lade-Sicherheitsnetz `materializeLegacyBestand` ab.
 *
 * Beide Schritte sind NICHT verlustfrei umkehrbar (entfernte Zuordnungen bzw.
 * der frühere Bestand-Zustand sind weg) — `downgrade` stempelt nur die Version
 * zurück.
 */
export const migrationV16ToV17: MigrationDefinition = {
  fromVersion: 16,
  toVersion: 17,

  upgrade(doc: any): any {
    const header = doc?.header;
    // Nur Punkt-Docs tragen `header.projects`; andere Doc-Typen bleiben
    // unangetastet (nur Versions-Stempel unten).
    if (header && Array.isArray(header.projects) && header.projects.length > 0) {
      // 1) Mehrfach-Projekt-Zuordnung → auf ein Projekt reduzieren.
      if (header.projects.length > 1) {
        const ownerId: string | undefined = doc.workflow?.projectId;
        const keep =
          (ownerId && header.projects.find((p: any) => p && p.id === ownerId))
          || header.projects[header.projects.length - 1];
        header.projects = [keep];
      }

      // 2) Bestand (kein Workflow-Status) + Projekt-Zuordnung → abgeschlossen.
      //    WICHTIG: einen ggf. schon vorhandenen Teil-Workflow (createdBy /
      //    editors / history) NICHT wegwerfen — sonst verliert der Punkt seinen
      //    Ersteller bzw. Inhaber, und der Eigentümer kommt an die eigenen Punkte
      //    nicht mehr heran (Editier-/Löschrecht hängt an `workflow.createdBy.id`).
      //    Der „migration"-Platzhalter greift nur, wenn GAR kein Ersteller
      //    dokumentiert ist (echter Bestand ohne Workflow-Block).
      if (!doc.workflow || !doc.workflow.status) {
        const projectId: string | undefined = header.projects[0]?.id;
        const time = Date.now();
        const existing = doc.workflow ?? {};
        const createdBy = existing.createdBy ?? { id: 'migration', time };
        const history = Array.isArray(existing.history) ? existing.history.slice() : [];
        history.push({
          kind: 'transition',
          from: null,
          to: 'abgeschlossen',
          time,
          user: 'Migration',
          note: 'Migration v16→v17: Bestandspunkt mit Projekt-Zuordnung als abgeschlossen festgeschrieben'
        });
        doc.workflow = {
          ...existing,
          status: 'abgeschlossen',
          lastStatusChange: { time, user: 'Migration' },
          createdBy,
          ...(projectId ? { projectId } : {}),
          history
        };
      }
    }
    doc.version = 17;
    return doc;
  },

  downgrade(doc: any): any {
    // Nicht verlustfrei umkehrbar — nur Versions-Rückstempel.
    doc.version = 16;
    return doc;
  },

  dataLossWarnings: [
    'Mehrfach-Projekt-Zuordnungen wurden auf ein Projekt reduziert — die entfernten Zuordnungen sind beim Downgrade nicht wiederherstellbar.',
    'Bestandspunkte mit Projekt-Zuordnung wurden als „abgeschlossen" festgeschrieben — der frühere Bestand-Zustand (ohne Workflow) ist beim Downgrade nicht wiederherstellbar.'
  ]
};
